import { useEffect, useId, useState } from "react";
import { feedbackItemKey, isRateable, REASON_MAX_LENGTH, type FeedbackItem, type FeedbackQueue, type FeedbackSource } from "./types";

export type QueueState =
  | { status: "loading" }
  | { status: "ready"; queue: FeedbackQueue }
  | { status: "unavailable"; message: string };

export interface FeedbackModuleProps {
  sources: FeedbackSource[];
  selectedSourceId: string;
  queueState: QueueState;
  savingItemIds: ReadonlySet<string>;
  actionError?: string;
  onSelectSource(sourceId: string): void;
  onRespond(item: FeedbackItem, value: string): void;
  onClear(item: FeedbackItem): void;
  onReason(item: FeedbackItem, reason: string): void;
  onRetry(): void;
  onExport?(): void;
}

/** Items in presentation order, split into runs sharing a batch.
 *
 * A source whose queue is one batch (or none) stays a single ungrouped run, so
 * a lone heading is never drawn over the whole list. Runs are cut from adjacent
 * items rather than collected into a map, which keeps presentation order intact
 * instead of reordering the list by batch.
 */
function groupByBatch(items: FeedbackItem[]): { batchId?: string; items: FeedbackItem[] }[] {
  if (new Set(items.map((item) => item.batch_id ?? "")).size <= 1) return [{ items }];
  const groups: { batchId?: string; items: FeedbackItem[] }[] = [];
  for (const item of items) {
    const current = groups.at(-1);
    if (current && (current.batchId ?? "") === (item.batch_id ?? "")) current.items.push(item);
    else groups.push({ batchId: item.batch_id, items: [item] });
  }
  return groups;
}

/** Items split into the runs their group headings introduce.
 *
 * A heading covers the items that follow it, so the runs are cut in
 * presentation order. Items arriving before any heading keep their place in an
 * unheaded run rather than being attached to a heading further down.
 */
function groupBySection(items: FeedbackItem[]): { heading?: FeedbackItem; items: FeedbackItem[] }[] {
  const sections: { heading?: FeedbackItem; items: FeedbackItem[] }[] = [];
  for (const item of items) {
    if (item.scope === "group") sections.push({ heading: item, items: [] });
    else if (sections.length > 0) sections.at(-1)!.items.push(item);
    else sections.push({ items: [item] });
  }
  return sections;
}

/** Render a `YYYY-MM-DD` batch id as a readable local date. */
function formatBatchLabel(batchId?: string): string {
  if (!batchId) return "Undated";
  const [year, month, day] = batchId.split("-").map(Number);
  if (!year || !month || !day) return batchId;
  // Built from parts on purpose. `new Date("2026-09-18")` parses as UTC
  // midnight, which renders as the previous day in any zone behind UTC.
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function answeredCount(items: FeedbackItem[]): number {
  return items.filter((item) => item.current_response !== undefined).length;
}

export function SourceNavigation({
  sources,
  selectedSourceId,
  onSelectSource,
}: Pick<FeedbackModuleProps, "sources" | "selectedSourceId" | "onSelectSource">) {
  return (
    <label className="block shrink-0">
      <span className="mb-1 block text-xs font-medium text-ink-muted">Source</span>
      <select
        aria-label="Feedback source"
        value={selectedSourceId}
        onChange={(event) => onSelectSource(event.target.value)}
        className="min-w-44 rounded-xl border border-border-subtle bg-surface px-3 py-2 text-sm text-ink"
      >
        {sources.map((source) => <option key={source.source_id} value={source.source_id}>{source.name}</option>)}
      </select>
    </label>
  );
}

/** Progress over the rows that take a verdict.
 *
 * A heading takes a note and no verdict, so counting it would make a queue
 * unfinishable: the bar would never fill however many items were rated.
 */
export function FeedbackProgress({ items }: { items: FeedbackItem[] }) {
  const rateable = items.filter(isRateable);
  const answered = answeredCount(rateable);
  const percentage = rateable.length === 0 ? 0 : (answered / rateable.length) * 100;
  return (
    <div className="mt-5 flex items-center gap-3" aria-label={`${answered} of ${rateable.length} answered`}>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-raised">
        <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${percentage}%` }} />
      </div>
      <span className="text-xs text-ink-muted">{answered}/{rateable.length}</span>
    </div>
  );
}

/** A free-text note on a row, collapsed behind its own button.
 *
 * Collapsed because a queue is read far more often than annotated: an open
 * field on every row buries the thing being reviewed. The button carries
 * whether a note is already there, so a collapsed panel never hides one. The
 * draft is local and committed on blur, since saving per keystroke would write
 * a partial sentence on every character.
 */
function NoteField({
  item,
  label,
  ariaLabel,
  saving,
  onReason,
}: {
  item: FeedbackItem;
  label: string;
  /** Needed where the visible label repeats down the page and names nothing. */
  ariaLabel?: string;
  saving: boolean;
  onReason(item: FeedbackItem, reason: string): void;
}) {
  const saved = item.current_reason ?? "";
  const [draft, setDraft] = useState(saved);
  const [open, setOpen] = useState(false);
  // Re-sync when the server hands back a different note for this item, which
  // happens after a save and when a re-fetch replaces the queue.
  useEffect(() => { setDraft(saved); }, [saved]);
  const panelId = useId();
  const inputId = `${panelId}-input`;
  const hasNote = draft.trim() !== "";
  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={ariaLabel}
        data-has-note={hasNote || undefined}
        onClick={() => setOpen((current) => !current)}
        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition ${hasNote ? "border-accent bg-accent text-surface-sunken" : "border-border-subtle bg-surface-sunken/40 text-ink-muted hover:border-ink-muted hover:text-ink"}`}
      >
        <span aria-hidden className="mr-1 inline-block">{open ? "▾" : "▸"}</span>{label}
      </button>
      <div id={panelId} hidden={!open} className="mt-2">
        <label htmlFor={inputId} className="mb-1 block text-xs font-medium text-ink-muted">
          {item.prompt ?? <>Why? <span className="font-normal">(optional)</span></>}
        </label>
        <textarea
          id={inputId}
          rows={2}
          value={draft}
          maxLength={REASON_MAX_LENGTH}
          disabled={saving}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => { if (draft.trim() !== saved) onReason(item, draft); }}
          className="w-full resize-y rounded-xl border border-border-subtle bg-surface-sunken/40 px-3 py-2 text-sm text-ink placeholder:text-ink-muted/60 disabled:opacity-50"
        />
        <p className="mt-1 text-right text-[11px] text-ink-muted">{draft.length}/{REASON_MAX_LENGTH}</p>
      </div>
    </div>
  );
}

/** A heading's long-form detail, collapsed behind its own button.
 *
 * Collapsed for the same reason the note is: a section's script runs to a
 * paragraph or two, and nine of them open at once buries the items they
 * introduce. Rendered pre-wrapped, since the blank lines in it are the
 * paragraphing it was written with.
 */
function DetailDisclosure({ detail, label, ariaLabel }: { detail: string; label: string; ariaLabel: string }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={ariaLabel}
        onClick={() => setOpen((current) => !current)}
        className="rounded-full border border-border-subtle bg-surface-sunken/40 px-2.5 py-1 text-xs font-medium text-ink-muted transition hover:border-ink-muted hover:text-ink"
      >
        <span aria-hidden className="mr-1 inline-block">{open ? "▾" : "▸"}</span>{label}
      </button>
      <div id={panelId} hidden={!open} className="mt-2 whitespace-pre-line border-l-2 border-border-subtle pl-3 text-sm leading-relaxed text-ink-muted">
        {detail}
      </div>
    </div>
  );
}

export function FeedbackItemCard({
  item,
  ordinal,
  prompt,
  saving,
  acceptsNoteOnly,
  onRespond,
  onClear,
  onReason,
}: {
  item: FeedbackItem;
  ordinal: number;
  prompt?: string;
  saving: boolean;
  /** Whether this source takes a note before a verdict is chosen. */
  acceptsNoteOnly: boolean;
  onRespond(item: FeedbackItem, value: string): void;
  onClear(item: FeedbackItem): void;
  onReason(item: FeedbackItem, reason: string): void;
}) {
  const answered = item.current_response !== undefined;
  const noteLabel = answered ? "Why this one?" : "Why?";
  return (
    <article className={`rounded-3xl border p-4 sm:p-5 ${answered ? "border-border-subtle/60 bg-surface/55" : "border-border-subtle bg-surface"}`}>
      {/* Dimmed above the verdict controls, which stay full strength: a row that
          did not make the episode should read as one at a glance, while still
          looking like something to rate rather than something disabled. */}
      <div className={`flex gap-3 ${item.badge ? "opacity-70" : ""}`}>
        <span className="text-xs text-ink-muted">{String(ordinal).padStart(2, "0")}</span>
        <div className="min-w-0 flex-1">
          {/* h4, below the batch and section headings: a day holds sections. */}
          {item.url ? <a href={item.url} target="_blank" rel="noreferrer" className="font-semibold text-ink hover:text-accent">{item.title} ↗</a> : <h4 className="font-semibold text-ink">{item.title}</h4>}
          {item.badge && (
            <span className="ml-2 whitespace-nowrap rounded-full border border-border-subtle bg-surface-sunken/60 px-2 py-0.5 align-middle text-[11px] font-medium uppercase tracking-wide text-ink-muted">
              {item.badge}
            </span>
          )}
          {item.subtitle && <p className="mt-1 text-xs text-ink-muted">{item.subtitle}</p>}
          {/* Pre-wrapped: a detail is the episode's narration, whose blank lines
              are the paragraphing it was spoken with. */}
          {item.detail && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-ink-muted">{item.detail}</p>}
        </div>
      </div>
      <div className="mt-4 sm:ml-7">
        {prompt && <p className="mb-2 text-xs font-medium text-ink-muted">{prompt}</p>}
        <div className="flex flex-wrap items-center gap-2">
          {item.response_options.map((option) => {
            const selected = item.current_response === option.value;
            return (
              <button
                key={option.value}
                type="button"
                disabled={saving}
                aria-pressed={selected}
                aria-label={option.aria_label}
                title={option.description}
                onClick={() => selected ? onClear(item) : onRespond(item, option.value)}
                className={`rounded-xl border px-3 py-2 text-xs font-semibold transition disabled:opacity-50 ${selected ? "border-accent bg-accent text-surface-sunken" : "border-border-subtle bg-surface-sunken/40 text-ink-muted hover:border-ink-muted hover:text-ink"}`}
              >
                {option.label}
              </button>
            );
          })}
          {answered && (
            <button type="button" disabled={saving} onClick={() => onClear(item)} className="ml-auto text-xs text-ink-muted underline underline-offset-4 hover:text-ink disabled:opacity-50">
              Clear response
            </button>
          )}
        </div>
        {(acceptsNoteOnly || answered) && (
          <NoteField
            item={item}
            label={noteLabel}
            // The visible label repeats down the queue, so the title tells a
            // reader which item's note this opens. It leads with the label
            // itself, which is what a voice command will say.
            ariaLabel={`${noteLabel} ${item.title}`}
            saving={saving}
            onReason={onReason}
          />
        )}
      </div>
    </article>
  );
}

/** One batch: its heading, its own note, and its sections in running order. */
function FeedbackBatch({
  batchId,
  items,
  source,
  savingItemIds,
  onRespond,
  onClear,
  onReason,
}: {
  batchId?: string;
  items: FeedbackItem[];
} & Pick<FeedbackModuleProps, "savingItemIds" | "onRespond" | "onClear" | "onReason"> & {
  source?: FeedbackSource;
}) {
  const batchNote = items.find((item) => item.scope === "batch");
  const rows = items.filter((item) => item.scope !== "batch");
  const rateable = rows.filter(isRateable);
  // The date disambiguates a heading's note from the same heading on another day.
  const headingNote = (heading: FeedbackItem) =>
    `Note on ${heading.title}${batchId ? `, ${formatBatchLabel(batchId)}` : ""}`;
  // Numbered over the items alone, so the sequence has no gaps where a heading
  // sits, and restarted per batch the way an episode's running order does.
  const ordinals = new Map(rateable.map((item, index) => [item.item_id, index + 1]));
  const saving = (item: FeedbackItem) => savingItemIds.has(feedbackItemKey(item));
  return (
    <section aria-label={batchId && formatBatchLabel(batchId)}>
      {batchId && (
        <div className="mt-8 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border-subtle pb-2">
          <h2 className="text-sm font-semibold text-ink">{formatBatchLabel(batchId)}</h2>
          {batchNote?.subtitle && <span className="text-xs text-ink-muted">{batchNote.subtitle}</span>}
          <span className="ml-auto text-xs text-ink-muted">{answeredCount(rateable)}/{rateable.length}</span>
        </div>
      )}
      {batchNote && <NoteField item={batchNote} label="Note" ariaLabel={headingNote(batchNote)} saving={saving(batchNote)} onReason={onReason} />}
      {groupBySection(rows).map((section, index) => (
        <div key={section.heading?.item_id ?? `section-${index}`}>
          {section.heading && (
            <div className="mt-6">
              <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border-subtle/60 pb-1.5">
                <h3 className="text-sm font-semibold text-ink">{section.heading.title}</h3>
                {section.heading.subtitle && <span className="text-xs text-ink-muted">{section.heading.subtitle}</span>}
              </div>
              {/* Stacked rather than side by side: both panels open to full
                  width, and a row of buttons would shrink each open panel to
                  its own column. */}
              {section.heading.detail && (
                <DetailDisclosure
                  detail={section.heading.detail}
                  label="Read script"
                  // The label repeats down the page, so the accessible name
                  // says which section's script this opens, and which day's.
                  ariaLabel={`Read script for ${section.heading.title}${batchId ? `, ${formatBatchLabel(batchId)}` : ""}`}
                />
              )}
              <NoteField item={section.heading} label="Note" ariaLabel={headingNote(section.heading)} saving={saving(section.heading)} onReason={onReason} />
            </div>
          )}
          <ol className="mt-3 space-y-3">
            {section.items.map((item) => (
              <li key={item.item_id}>
                <FeedbackItemCard
                  item={item}
                  ordinal={ordinals.get(item.item_id) ?? 0}
                  prompt={source?.prompt}
                  saving={saving(item)}
                  acceptsNoteOnly={source?.supports_note_only ?? false}
                  onRespond={onRespond}
                  onClear={onClear}
                  onReason={onReason}
                />
              </li>
            ))}
          </ol>
        </div>
      ))}
    </section>
  );
}

export function FeedbackModule(props: FeedbackModuleProps) {
  const source = props.sources.find((candidate) => candidate.source_id === props.selectedSourceId);
  const orderedItems = props.queueState.status === "ready"
    ? [...props.queueState.queue.items].sort((left, right) => left.presentation_order - right.presentation_order)
    : [];
  return (
    <section aria-labelledby="review-heading">
      <div className="flex flex-col gap-5 border-b border-border-subtle pb-6 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm font-medium text-ink-muted">Feedback queue</p>
          <h1 id="review-heading" className="mt-0.5 text-3xl font-semibold tracking-tight text-ink">Review</h1>
          <p className="mt-2 max-w-lg text-sm text-ink-muted">{source?.description ?? "Tune future recommendations one response at a time."}</p>
        </div>
        <SourceNavigation {...props} />
      </div>

      {props.queueState.status === "loading" && <p className="py-12 text-center text-sm text-ink-muted">Loading feedback…</p>}
      {props.queueState.status === "unavailable" && (
        <div className="mt-6 rounded-2xl border border-danger/40 bg-danger/10 p-5">
          <p role="alert" className="text-sm text-ink">{props.queueState.message}</p>
          <button type="button" onClick={props.onRetry} className="mt-3 text-sm font-semibold text-accent">Try again</button>
        </div>
      )}
      {props.queueState.status === "ready" && (
        <>
          <FeedbackProgress items={orderedItems} />
          {orderedItems.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-border-subtle bg-surface p-8 text-center">
              <p className="font-semibold text-ink">Nothing to review</p>
              <p className="mt-1 text-sm text-ink-muted">This source has no current items.</p>
            </div>
          ) : (
            groupByBatch(orderedItems).map((group, index) => (
              <FeedbackBatch
                key={group.batchId ?? `batch-${index}`}
                batchId={group.batchId}
                items={group.items}
                source={source}
                savingItemIds={props.savingItemIds}
                onRespond={props.onRespond}
                onClear={props.onClear}
                onReason={props.onReason}
              />
            ))
          )}
          <div className="mt-5 flex items-center justify-between">
            <p className="text-xs text-ink-muted">Changes save immediately</p>
            {source?.supports_export && props.onExport && <button type="button" onClick={props.onExport} className="text-xs text-ink-muted underline underline-offset-4 hover:text-accent">Export feedback</button>}
          </div>
          {props.actionError && <p role="alert" className="mt-3 text-sm text-danger">{props.actionError}</p>}
        </>
      )}
    </section>
  );
}
